package com.nonopn.skywatcher.common.app.pages.map.model.weather

import androidx.compose.ui.graphics.Color
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.uv_index_extreme
import com.nonopn.skywatcher.common.app.res.uv_index_high
import com.nonopn.skywatcher.common.app.res.uv_index_low
import com.nonopn.skywatcher.common.app.res.uv_index_moderate
import com.nonopn.skywatcher.common.app.res.uv_index_very_high
import com.nonopn.skywatcher.design.theme.AppColor
import kotlinx.serialization.Serializable
import org.jetbrains.compose.resources.StringResource

@Serializable
data class Condition(
    val timestamp: Long,
    /**
     * The temperature in Celsius. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val tempC: Float,
    /**
     * The feels-like / apparent temperature in Celsius. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     *
     * The feels-like temperature is often based on a combination of the NOAA heat index and/or wind chill specifications.
     *
     * Locations with a temperature of 80F (26.67C) or higher will high humidity will have a feels-like temperature higher than the air temperature. Likewise, locations with a temperature of 40F (4.44C) and lower winds speeds of 3 mph or higher will often have a feels like temperature less than the air temperature.
     */
    val feelslikeC: Float,
    /**
     * The dew point in Celsius. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val dewpointC: Float,
    /**
     * The relative humidity, from 0 - 100. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val humidity: Int,
    /**
     * The mean sea level pressure (MSLP) in millibars. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val pressureMB: Double,
    /**
     * The wind direction, Null if unavailable:
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip.
     */
    val windDir: StormDirectionAbbreviated,
    /**
     * The wind speed in degrees (from 0 to 360). Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val windDirDEG: Int,
    /**
     * The wind speed in kilometers per hour. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val windSpeedKPH: Float,
    /**
     * The wind gusts in kilometers per hour. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val windGustKPH: Float,
    /**
     * The estimated precipitation over the selected period and is fluid based on the value passed into the filter parameter.
     *
     * The default is mm/hour for hourly and point in time requests.
     *
     * However, if making a sub-hourly request, the value will be the amount over the minutely interval selected. For example, 1-minute intervals will be for 1 minute. If filter=15min is utilized, then it will be the total over each 15-minute interval in the response.
     *
     * Null if unavailable.
     */
    val precipMM: Double,
    /**
     * The precipitation rate in mm/hour at the point in time of the period.
     *
     * Null if unavailable.
     */
    val precipRateMM: Double,
    /**
     * The estimated snowfall over the selected period and is fluid based on the value passed into the filter parameter.
     *
     * The default is cm/hour for hourly and point in time requests.
     *
     * However, if making a sub-hourly request, the value will be the amount over the minutely interval selected. For example, 1-minute intervals will be for 1 minute. If filter=15min is utilized, then it will be the total over each 15-minute interval in the response.
     *
     * Null if unavailable.
     */
    val snowCM: Int,
    /**
     * The snowfall rate in cm/hour at the point in time of the period.
     *
     * Null if unavailable.
     */
    val snowRateCM: Int,
    /**
     * The estimated snow depth in cm at the point in time of the period. Historical data for snow depth is available after September, 7th 2023.
     *
     * Null if unavailable.
     */
    val snowDepthCM: Int,
    /**
     * Probability of precipitation. A percentage from 0 - 100%. Available for requests for the current time and forecast requests. Null for archive requests or if unavailable.
     */
    val pop: Int,
    /**
     * The visibility in kilometers. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val visibilityKM: Double,
    /**
     * The percentage of the clouds in the sky. From 0 - 100. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val sky: Int,
    /**
     * Coded version of cloud conditions. See the Coded Weather documentation for code options. Null if unavailable.
     *
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val cloudsCoded: String,
    /**
     * Phrase of weather conditions. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val weather: String,
    /**
     * Coded version of weather conditions. See the Coded Weather documentation for code options. Null if unavailable.
     *
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val weatherCoded: String,
    /**
     * Phrase of the primary weather conditions. Null if unavailable.
     */
    val weatherPrimary: String,
    /**
     * Coded version of the primary weather conditions. See the Coded Weather documentation for code options. Null if unavailable.
     * https://www.xweather.com/docs/weather-api/reference/weather-codes
     */
    val weatherPrimaryCoded: String,
    /**
     * Icon name corresponding to the primary weather conditions.
     */
    val icon: String,
    /**
     * Ultraviolet Index (from 1 to 12). Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val uvi: Int,
    /**
     * Signifies if the observation occurred during daily hours. True if between sunrise and sunset, false otherwise.
     */
    val isDay: Boolean,
    /**
     * The surface pressure in millibars. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val spressureMB: Double,
    /**
     * The altimeter value in millibars. Null if unavailable.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val altimeterMB: Double,
    val solrad: SolRad
) {
    fun actualWeatherCoded() = WeatherCoded(weatherCoded)

    fun actualWeatherPrimaryCoded() = WeatherCoded(weatherPrimaryCoded)

    val actualUVIndex: UVIndex
        get() {
            val found = UVIndex.entries.firstOrNull { it.threshold >= uvi }

            return found ?: UVIndex.Low
        }
}

interface DisplayableIndex {
    val threshold: Int
    val color: Color
    val textColor: Color
    val colorSub: Color
    val text: StringResource
}

@Serializable
enum class UVIndex(
    override val threshold: Int,
    override val color: Color,
    override val textColor: Color,
    override val colorSub: Color,
    override val text: StringResource
) : DisplayableIndex {
    Low(
        2, AppColor.Green, AppColor.WhiteCream, AppColor.GreenSemiTransparent,
        Res.string.uv_index_low
    ),
    Moderate(
        5, AppColor.Yellorange, AppColor.WhiteCream, AppColor.YellorangeSemiTransparent,
        Res.string.uv_index_moderate
    ),
    High(
        7, AppColor.Orange, AppColor.WhiteCream, AppColor.OrangeSemiTransparent,
        Res.string.uv_index_high
    ),
    VeryHigh(
        10, AppColor.Red, AppColor.WhiteCream, AppColor.RedSemiTransparent,
        Res.string.uv_index_very_high
    ),
    Extreme(
        12, AppColor.RedDark, AppColor.WhiteCream, AppColor.RedDarkSemiTransparent,
        Res.string.uv_index_extreme
    )
}

@Serializable
data class SolRad(
    /**
     * The solar azimuth angle.
     */
    val azimuthDEG: Double,
    /**
     * The solar zenith angle.
     */
    val zenithDEG: Double,
    /**
     * Estimated 1-hour Global Horizontal Irradiance (global solar radiation) in watts/meter squared.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val ghiWM2: Double,
    /**
     * Estimated 1-hour Direct Normal Irradiation in watts/meter squared.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val dniWM2: Double,
    /**
     * Estimated 1-hour Diffused Horizontal Irradiance in watts/meter squared.
     * Not returned with minutely precipitation forecasts (filter=minutelyprecip).
     */
    val dhiWM2: Double
)
